/** Render dinámico de variables técnicas configurables por familia (con dependencias). */
import React, { useState, useEffect } from 'react';
import api from '@/api/axios';
import { Input } from '@/components/ui/input';

const norm = (v) => (typeof v === 'boolean' ? (v ? 'si' : 'no') : String(v ?? '').trim().toLowerCase());
const NUMERICOS = ['numero', 'decimal', 'entero', 'medida', 'area', 'longitud', 'cantidad', 'moneda'];

export const VariablesTecnicas = ({ familia, valores, onChange }) => {
  const [vars, setVars] = useState([]);
  useEffect(() => {
    if (!familia) return;
    api.get('/sa/variables', { params: { familia } }).then((r) => setVars(r.data.variables || [])).catch(() => setVars([]));
  }, [familia]);

  const skip = ['ancho_m', 'alto_m']; // ya capturadas en la sección Medidas
  const visible = (v) => v.activa !== false && (!v.depende_de?.variable || norm(valores[v.depende_de.variable]) === norm(v.depende_de.valor));
  const lista = vars.filter((v) => !skip.includes(v.codigo) && visible(v));
  if (!lista.length) return null;

  const set = (codigo, val) => onChange({ ...valores, [codigo]: val });

  const campo = (v) => {
    const val = valores[v.codigo] ?? v.valor_default ?? '';
    const tid = `var-${v.codigo}`;
    if (v.tipo === 'si_no') {
      return (
        <select value={norm(val) || 'no'} onChange={(e) => set(v.codigo, e.target.value)}
          className="w-full h-9 text-sm border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-2" data-testid={tid}>
          <option value="no">No</option>
          <option value="si">Sí</option>
        </select>
      );
    }
    if (v.tipo === 'seleccion') {
      if (!(v.opciones || []).length) {
        return <p className="h-9 flex items-center text-xs italic text-amber-500" data-testid={tid}>Pendiente de configurar</p>;
      }
      return (
        <select value={val} onChange={(e) => set(v.codigo, e.target.value)}
          className="w-full h-9 text-sm border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-2" data-testid={tid}>
          <option value="">—</option>
          {(v.opciones || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (v.tipo === 'seleccion_multiple') {
      const sel = Array.isArray(val) ? val : [];
      return (
        <div className="flex flex-wrap gap-1.5" data-testid={tid}>
          {(v.opciones || []).map((o) => (
            <button key={o} type="button"
              onClick={() => set(v.codigo, sel.includes(o) ? sel.filter((x) => x !== o) : [...sel, o])}
              className={`px-2 py-1 rounded-full text-xs font-semibold border ${sel.includes(o) ? 'border-[#0EA5E9] text-[#0EA5E9] bg-[#0EA5E9]/10' : 'border-[var(--border)] text-[var(--text-muted)]'}`}>
              {o}
            </button>
          ))}
        </div>
      );
    }
    if (v.tipo === 'fecha') return <Input type="date" value={val} onChange={(e) => set(v.codigo, e.target.value)} className="h-9" data-testid={tid} />;
    if (NUMERICOS.includes(v.tipo)) return <Input type="number" step="any" value={val} onChange={(e) => set(v.codigo, e.target.value)} className="h-9" data-testid={tid} />;
    return <Input value={val} onChange={(e) => set(v.codigo, e.target.value)} className="h-9" data-testid={tid} />;
  };

  return (
    <div data-testid="variables-tecnicas">
      <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)] mb-1.5">Medidas y opciones personalizadas</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {lista.map((v) => (
          <div key={v.codigo} className={v.tipo === 'seleccion_multiple' ? 'col-span-2 sm:col-span-3' : ''}>
            <label className="text-xs font-bold text-[var(--text-muted)]">
              {v.nombre}{v.unidad ? ` (${v.unidad})` : ''}{v.obligatoria ? ' *' : ''}
            </label>
            {campo(v)}
            {v.ayuda && <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{v.ayuda}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};
